import { useState } from "react";
import {
  Loader2, Globe, MessageSquare, AlertCircle, X,
  CheckCircle2, ShieldCheck, BarChart3, Filter,
} from "lucide-react";
import { useGetOpenExamDietsQuery } from "../../api/examsApi";
import {
  useChairmanGetResultsQuery,
  useChairmanPublishResultsMutation,
  useChairmanRequestReviewMutation,
} from "../../api/chairmanExamApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function extractError(err: any): string {
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

const STATUS_MAP: Record<string, string> = {
  approved:         "bg-emerald-50 text-emerald-700 border-emerald-100",
  under_moderation: "bg-purple-50 text-purple-700 border-purple-100",
  published:        "bg-emerald-50 text-emerald-700 border-emerald-100",
  rework:           "bg-rose-50 text-rose-700 border-rose-100",
};

type ReviewState = { id: string; comment: string };

export default function ChairmanModerationPage() {
  const { data: diets = [], isLoading: dietsLoading } = useGetOpenExamDietsQuery();
  const [dietFilter, setDietFilter] = useState("");

  const { data: results = [], isLoading } = useChairmanGetResultsQuery(
    dietFilter ? { dietId: dietFilter } : undefined
  );
  const [publishResults, { isLoading: publishing }] = useChairmanPublishResultsMutation();

  const { can } = useAccess();
  const mayPublish = can.perm(PERMS.exam.resultPublish);
  const [requestReview,  { isLoading: reviewing }]  = useChairmanRequestReviewMutation();

  const [actionError, setActionError] = useState<Record<string, string>>({});
  const [reviewPanel, setReviewPanel] = useState<ReviewState | null>(null);

  const handlePublish = async (id: string) => {
    setActionError(p => ({ ...p, [id]: "" }));
    try { await publishResults(id).unwrap(); }
    catch (err) { setActionError(p => ({ ...p, [id]: extractError(err) })); }
  };

  const handleReview = async () => {
    if (!reviewPanel) return;
    const { id, comment } = reviewPanel;
    setActionError(p => ({ ...p, [id]: "" }));
    if (!comment.trim()) {
      setActionError(p => ({ ...p, [id]: "Please provide a reason for returning to the Chief Examiner." }));
      return;
    }
    try {
      await requestReview({ id, comment }).unwrap();
      setReviewPanel(null);
    } catch (err) { setActionError(p => ({ ...p, [id]: extractError(err) })); }
  };

  const selCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  const pending   = (results as any[]).filter((r: any) => ["approved","under_moderation"].includes(r.status)).length;
  const published = (results as any[]).filter((r: any) => r.status === "published").length;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Total Batches", val: (results as any[]).length, icon: BarChart3,   color: "indigo"  },
          { label: "Pending",       val: pending,                   icon: AlertCircle, color: "amber"   },
          { label: "Published",     val: published,                 icon: Globe,       color: "emerald" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <BarChart3 className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Moderation Board</h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">Review approved batches and publish results.</p>
            </div>
          </div>

          <div className="space-y-1 w-full sm:w-64">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
              <Filter className="w-3 h-3" /> Filter by Diet
            </label>
            {dietsLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
            ) : (
              <select value={dietFilter} onChange={(e) => setDietFilter(e.target.value)} className={selCls}>
                <option value="">All diets</option>
                {diets.map((d: any) => <option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Results list */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Result Batches {(results as any[]).length > 0 ? `(${(results as any[]).length})` : ""}
          </div>
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
          </div>
        ) : (results as any[]).length === 0 ? (
          <div className="py-24 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No results pending moderation</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {(results as any[]).map((r: any) => {
              const status = (r.status ?? "approved").toLowerCase();
              const canAct = status === "approved" || status === "under_moderation";
              return (
                <div key={r.id} className="group">
                  <div className="flex items-start justify-between px-8 py-5 hover:bg-gray-50/80 transition-all gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">
                        {r.course_name ?? `Course ${r.course_id}`}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                        Diet: {r.diet_name ?? r.diet_id} &nbsp;·&nbsp;
                        Chief: {r.chief_name ?? "—"} &nbsp;·&nbsp;
                        {r.student_count ?? 0} students
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {status === "published" && (
                        <div className="flex items-center gap-1 text-emerald-600">
                          <Globe className="w-3.5 h-3.5" />
                          <span className="text-[9px] font-black uppercase tracking-widest">Live</span>
                        </div>
                      )}
                      <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm ${STATUS_MAP[status] ?? "bg-gray-50 text-gray-400 border-gray-100"}`}>
                        {status.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>

                  {canAct && (
                    <div className="px-8 pb-5 flex flex-wrap items-center gap-3">
                      {actionError[r.id] && (
                        <p className="w-full text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> {actionError[r.id]}
                        </p>
                      )}
                      {/* Reading this board is exam.moderation.access, which is
                          what opens the route. Publishing a batch — the legacy
                          path by which marks become final — and sending it back
                          are both exam.result.publish. */}
                      {mayPublish && (
                        <>
                          <button onClick={() => handlePublish(r.id)} disabled={publishing}
                            className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-emerald-600 transition-all shadow-sm disabled:opacity-30 active:scale-95">
                            {publishing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                            Publish Results
                          </button>
                          <button onClick={() => setReviewPanel({ id: r.id, comment: "" })}
                            className="inline-flex items-center gap-2 border border-amber-200 text-amber-700 rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-amber-50 transition-all active:scale-95">
                            <MessageSquare className="w-3.5 h-3.5" /> Send Back to Chief
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {reviewPanel !== null && reviewPanel.id === r.id && (
                    <div className="mx-8 mb-5 p-5 bg-amber-50 rounded-2xl border border-amber-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">Send Back — Reason</p>
                        <button onClick={() => setReviewPanel(null)} className="p-1.5 hover:bg-amber-100 rounded-xl">
                          <X className="w-3.5 h-3.5 text-amber-600" />
                        </button>
                      </div>
                      <textarea rows={3}
                        value={reviewPanel.comment}
                        onChange={(e) => setReviewPanel(p => p ? { ...p, comment: e.target.value } : null)}
                        className="w-full border-b-2 border-amber-200 py-2 font-bold text-gray-800 outline-none focus:border-amber-500 bg-transparent text-sm resize-none"
                        placeholder="Explain the reason for returning to the Chief Examiner…"
                      />
                      <div className="flex gap-3">
                        <button onClick={handleReview} disabled={reviewing}
                          className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-amber-600 transition-all disabled:opacity-30 active:scale-95">
                          {reviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                          {reviewing ? "Sending…" : "Send Back"}
                        </button>
                        <button onClick={() => setReviewPanel(null)}
                          className="py-3 px-5 text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-8 h-8 text-indigo-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">Publication Protocol</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Published results become visible to all registered candidates. This action is irreversible.
            Ensure all scores have been verified before publishing.
          </p>
        </div>
      </div>
    </div>
  );
}
